import * as React from "react";
import {
  Dimensions,
  ScrollView,
  StatusBar,
  StyleSheet,
  TextInput,
  Platform,
} from "react-native";
import Colors from "../../constants/Colors";
import { View, Text } from "../../components/Themed";
import PageHeader from "../../components/PageHeader";
import { FormProvider, useForm } from "react-hook-form";
import Popup from "../../components/Popup";
import { useEffect, useState } from "react";
import Icon from "../../components/Icon";
import SignIn from "../SignIn";
import { getLocalData, storeLocalData } from "../../services/storage";
import { useFocusEffect } from "@react-navigation/native";
import Toast from "react-native-toast-message";
import GlobalContext from "../../context/Global";
import QRCode from "react-native-qrcode-svg";
import Pressable from "../../components/Pressable";
import queryClient from "../../services/reactQuery";
import Constants from "expo-constants";
import { useGetFormQuestions } from "../../hooks/useGetFormQuestions";
import LiteSignIn from "../SignIn/liteSignIn";
import { useSyncUnsyncedAttendees } from "../../hooks/useSyncUnSyncedAttendees";

import { getNavigationPrefix } from "../../utils/urls";

const NAVIGATION_PREFIX = getNavigationPrefix();

const width = Dimensions.get("window").width;

export const OpenHouseCheckIn = ({ navigation, route }: any) => {
  const syncAttendees = useSyncUnsyncedAttendees();
  const formMethods = useForm();

  const { isTablet, user } = React.useContext(GlobalContext);
  const { data: questions } = useGetFormQuestions(
    route?.params?.openHouse?.form_uuid,
    false
  );
  const [currentStep, setCurrentStep] = useState(0);
  const [showLockModal, setShowLockModal] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [change, setChange] = useState(false);
  const [pin, setPin] = useState("");
  const [propertyDetails, setPropertyDetails] = useState({});

  React.useEffect(() => {
    if (currentStep === 0) {
      syncAttendees(route?.params?.openHouse?.uuid);
    }
  }, [currentStep]);

  useEffect(() => {
    setIsLocked(route?.params?.unlock);
  }, [route?.params?.unlock]);

  useFocusEffect(
    React.useCallback(() => {
      setIsLocked(route?.params?.unlock);
      setPropertyDetails(route?.params?.openHouse?.propertyDetails);
    }, [route?.params]) // Add dependencies here
  );

  const onLockPressed = () => {
    if (isLocked) {
      setShowUnlockModal(true);
    } else {
      setShowLockModal(true);
    }
  };

  const restartForm = () => {
    if (currentStep === 0) setChange(!change);
    setCurrentStep(0);
  };

  const RestartFormButton = () => (
    <Pressable
      style={
        Platform.OS === "web"
          ? styles.restartFormTextWeb
          : styles.restartFormText
      }
      onPress={() => restartForm()}
    >
      <Text
        size={Platform.OS === "web" ? 16 : 20}
        lineHeight={28}
        weight={"Bold"}
        lightColor={Colors.primary_500}
        darkColor={Colors.primary_500}
      >
        Restart Form
      </Text>
    </Pressable>
  );

  const BackButton = () =>
    !isLocked ? (
      <Icon
        name="arrow-left"
        color={Colors.primary_500}
        size={24}
        onPress={async () => {
          if (currentStep === 0) {
            navigation.goBack();
            return;
          }
          await restartForm();
          await queryClient.refetchQueries(["getTags"]);
          await queryClient.invalidateQueries([
            "openHouseDetails",
            route.params.openHouse.uuid,
          ]);
          setTimeout(() => {
            navigation.goBack();
          }, 1000);
        }}
      />
    ) : (
      <></>
    );

  const PageTitleContainer = (props: any) => (
    <View style={styles.pageTitleContainer}>{props.children}</View>
  );

  const PageTitle = () => (
    <Text
      size={18}
      lineHeight={24}
      weight="SemiBold"
      lightColor={Colors.nuetral_grey_700}
    >
      Open House Sign In
    </Text>
  );

  const LockUnlockIcon = () => (
    <Icon
      name={isLocked ? "lock" : "unlock"}
      color={Colors.primary_500}
      size={24}
      onPress={() => onLockPressed()}
    />
  );

  const HeaderContainer = (props: any) => (
    <View
      style={[
        styles.headerContainer,
        {
          flexDirection: isTablet ? "row" : "column",
          alignItems: "center",
        },
        Platform.OS === "web"
          ? {
              marginTop: isTablet ? 0 : -16,
              width: "75%",
              alignSelf: "center",
            }
          : {},
      ]}
    >
      {props.children}
    </View>
  );

  const HeaderTextContainer = (props: any) => (
    <View style={styles.headerText}>{props.children}</View>
  );

  const Address = (props: any) => (
    <Text size={16} lineHeight={20} lightColor={Colors.nuetral_grey_700}>
      {props.children}
    </Text>
  );

  const RestartFormButtonContainer = (props: any) => (
    <View
      style={[
        styles.restartButtonContainer,
        { paddingBottom: !isTablet ? 34 : 0 },
      ]}
    >
      {props.children}
    </View>
  );

  const lockScreen = async () => {
    await storeLocalData("@pin", pin);
    setShowLockModal(false);
    setIsLocked(true);
  };

  const [count, setCount] = React.useState(0);

  const unlockScreen = async () => {
    const storedPin = await getLocalData("@pin");
    if (storedPin) {
      if (pin === storedPin) {
        setCount(0);
        setShowUnlockModal(false);
        setIsLocked(false);
      } else {
        setCount(count + 1);
        if (count >= 5) {
          setPin("");
          Toast.show({
            type: "error",
            position: "top",
            text1: "Seems You Forgot Your Pin",
            text2: `Your pin is ${storedPin}`,
            visibilityTime: 5000,
          });
        } else {
          Toast.show({
            type: "error",
            position: "top",
            text1: "Incorrect Pin",
            text2: "Please try again",
            visibilityTime: 1800,
          });
          setPin("");
        }
      }
    }
  };

  const WebRightComponent = () => (
    <View
      style={{
        flexDirection: "row",
        gap: 16,
        alignItems: "center",
      }}
    >
      <RestartFormButton />
      <LockUnlockIcon />
    </View>
  );

  return (
    <FormProvider {...formMethods}>
      <View
        lightColor={Colors.background}
        darkColor={Colors.nuetral_grey_600}
        style={styles.container}
      >
        <StatusBar hidden={true} />
        <PageHeader
          leftComponent={<BackButton />}
          centerComponent={
            <PageTitleContainer>
              <PageTitle />
            </PageTitleContainer>
          }
          rightComponent={
            Platform.OS !== "web" ? <LockUnlockIcon /> : <WebRightComponent />
          }
        />
        <ScrollView
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ paddingBottom: 100 }}
        >
          <HeaderContainer>
            {isTablet && <RestartFormButton />}
            <HeaderTextContainer>
              <Address>{`${propertyDetails?.street_address} ${propertyDetails?.city} ${propertyDetails?.state}, ${propertyDetails?.postal_code}`}</Address>
            </HeaderTextContainer>
            <View style={{ flex: 1 }}></View>
          </HeaderContainer>
          <View
            lightColor={Colors.base_colors_white}
            darkColor={Colors.nuetral_grey_700}
            style={[
              styles.signInFormContainer,
              Platform.OS === "web"
                ? {
                    width: "80%",
                    alignSelf: "center",
                  }
                : null,
            ]}
          >
            {questions && questions?.length === 1 ? (
              <LiteSignIn
                openHouse={route?.params?.openHouse}
                step={currentStep}
                setStep={setCurrentStep}
                change={change}
              />
            ) : (
              <SignIn
                openHouse={route?.params?.openHouse}
                step={currentStep}
                setStep={setCurrentStep}
                change={change}
              />
            )}
          </View>
          {currentStep === 0 && route?.params?.openHouse?.check_in_key && (
            <View
              style={{
                width: "100%",
                flexDirection: "row",
                justifyContent: "center",
              }}
            >
              <View
                style={{
                  marginTop: 20,
                  padding: isTablet ? 30 : 20,
                  paddingVertical: 40,
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "80%",
                  backgroundColor: Colors.base_colors_white,
                }}
              >
                <Text
                  style={{
                    marginBottom: 10,
                    color: Colors.nuetral_grey_700,
                    fontFamily: "NunitoSemiBold",
                    fontSize: isTablet ? 20 : 18,
                  }}
                >
                  Scan to sign in on your own device.
                </Text>
                <Text
                  style={{
                    marginBottom: 30,
                    color: Colors.nuetral_grey_700,
                    fontFamily: "NunitoRegular",
                  }}
                >
                  Use the QR code to sign in on your own device as you make your
                  way through the open house.{" "}
                </Text>
                <QRCode
                  size={158}
                  value={`${NAVIGATION_PREFIX}/?id=${
                    route?.params?.openHouse?.check_in_key
                  }&team=${encodeURIComponent(
                    user?.organization_info?.team || ""
                  )}&phone=${encodeURIComponent(
                    user?.managed_phone_number || ""
                  )}`}
                />
              </View>
            </View>
          )}
          {!isTablet && Platform.OS !== "web" && (
            <RestartFormButtonContainer>
              <RestartFormButton />
            </RestartFormButtonContainer>
          )}
        </ScrollView>

        {(showLockModal || showUnlockModal) && (
          <Popup
            show={showLockModal || showUnlockModal}
            header={
              <Text
                size={16}
                weight="Bold"
                lightColor={Colors.nuetral_grey_700}
              >
                {showLockModal ? "Enter a 4-digit PIN" : "Unlock Sign In"}
              </Text>
            }
            body={
              <View style={{ gap: 8 }}>
                <Text
                  style={{ textAlign: "center" }}
                  size={16}
                  lightColor={Colors.nuetral_grey_700}
                >
                  {showLockModal
                    ? "You will need this PIN to unlock your screen after it is locked."
                    : "Enter the pin you created when locking this form."}
                </Text>
                <TextInput
                  placeholder="Enter Pin"
                  style={{
                    backgroundColor: Colors.base_colors_white,
                    padding: 8,
                    borderRadius: 4,
                    borderColor: Colors.nuetral_grey_400,
                    borderWidth: 1,
                  }}
                  keyboardType="numeric"
                  maxLength={4}
                  onChangeText={(e) => setPin(e)}
                  allowFontScaling={false}
                  maxFontSizeMultiplier={1}
                />
              </View>
            }
            footer={
              <View
                lightColor={Colors.base_colors_white}
                darkColor={Colors.nuetral_grey_700}
                style={{
                  height: 55,
                  flexDirection: "row",
                  borderTopColor: Colors.tinted_grey_300,
                  borderTopWidth: 1,
                  paddingVertical: 8,
                }}
              >
                <Pressable
                  style={{
                    flex: 1,
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onPress={() => {
                    setCount(0),
                      setShowLockModal(false),
                      setShowUnlockModal(false);
                  }}
                >
                  <Text lightColor={Colors.nuetral_grey_700} weight="Bold">
                    Cancel
                  </Text>
                </Pressable>
                <Pressable
                  style={{
                    flex: 1,
                    alignItems: "center",
                    justifyContent: "center",
                    borderLeftColor: Colors.tinted_grey_300,
                    borderLeftWidth: 1,
                  }}
                  disabled={pin.length !== 4}
                  onPress={async () => {
                    if (showLockModal) {
                      lockScreen();
                    } else {
                      unlockScreen();
                    }
                  }}
                >
                  <Text lightColor={Colors.primary_500} weight="Bold">
                    {showLockModal ? "Lock" : "Unlock"}
                  </Text>
                </Pressable>
              </View>
            }
          ></Popup>
        )}
      </View>
    </FormProvider>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  headerContainer: {
    paddingHorizontal: 24,
    paddingVertical: 32,
  },
  pageTitleContainer: {
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  progressBarContainer: {
    paddingHorizontal: 50,
  },
  restartButtonContainer: {
    alignItems: "center",
    paddingTop: 32,
  },

  signInFormContainer: {
    paddingVertical: 40,
    alignItems: "center",
  },
  houseNumbers: {
    flexDirection: "row",
    gap: 16,
  },
  signInForm: {
    width: width,
    gap: 32,
  },
  form: {
    width: width,
    paddingHorizontal: 24,
    gap: 32,
  },
  signInFormHeader: {
    alignItems: "center",
    gap: 16,
  },
  signInFormControls: {
    gap: 16,
    paddingHorizontal: 64,
  },
  qrCodeContainer: {
    alignSelf: "center",
    marginTop: 32,
    paddingHorizontal: 30,
    paddingVertical: 40,
    width: "70%",
    flexDirection: "row",
    borderWidth: 1,
    borderColor: Colors.nuetral_grey_400,
  },
  qrCodeContainerText: {
    alignItems: "center",
    flex: 0.7,
    gap: 16,
  },
  qRCodeInstructions: {
    textAlign: "center",
  },
  qrCodeContainerImage: {
    flex: 0.3,
    alignItems: "center",
    justifyContent: "center",
  },
  headerText: {
    alignItems: "center",
    gap: 3,
  },
  restartFormText: {
    flex: 1,
  },
  restartFormTextWeb: {
    flex: 1,
    borderWidth: 2,
    borderRadius: 25,
    padding: 4,
    paddingLeft: 18,
    paddingRight: 18,
    borderColor: Colors.primary_500,
    marginRight: 16,
  },
});
